// src/pages/offlineSales/OfflineSalesOrderListPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShoppingBag, Eye, Printer, XCircle, CreditCard, RotateCcw, RefreshCw, X,
  Download, Loader2, CheckCircle, AlertCircle, Search, User, Plus, Pencil,
  Phone, Store, Calendar, Package, Clock, Wallet, UserCheck, Truck, Copy, Check
} from 'lucide-react';
import DocNumberCell from '../procurement/components/DocNumberCell';
import tb from '../procurement/components/ProcurementTable.module.css';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getOrders, getOrder, voidOrder } from '../../services/api/offlineSales/orders';
import OrderReceiptPrintModal from './components/OrderReceiptPrintModal';
import OfflineSalesOrderDetailsModal from './components/OfflineSalesOrderDetailsModal';
import OfflineSalesPaymentRecordsModal from './components/OfflineSalesPaymentRecordsModal';
import AirwaybillTrackingModal from '../orders/components/AirwaybillTrackingModal';
import { getCustomers } from '../../services/api/offlineSales/customers';
import { getShops } from '../../services/api/shops';
import { getBusinessCouriers, getCourierStatuses } from '../../services/api/businessCourier';
import { formatDate, formatTime, formatDateTime } from '../../utils/dateUtils';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import DateRangePicker from '../../components/ui/DateRangePicker/DateRangePicker';
import TruncatedText from '../../components/ui/TruncatedText/TruncatedText';
import SessionSelector from '../../components/ui/SessionSelector/SessionSelector';
import styles from './OfflineSalesOrderListPage.module.css';

// When selected, the backend receives courierStatusNotUpdated=true instead of courierStatusIds.
const NOT_UPDATED_VALUE = '__NOT_UPDATED__';

const EMPTY_FILTERS = {
  search: '',
  statusKeys: [],
  paymentStatus: '',
  shopIds: [],
  businessCourierIds: [],
  courierStatusIds: [],
  dateFrom: '',
  dateTo: '',
  customerId: '',
  sessionId: '',
};

// ─── Status / payment badge helpers ───────────────────────────────────────────

const PAYMENT_STATUS_ICONS = {
  unpaid: Clock,
  partial: Wallet,
  paid: CheckCircle,
};

const PAYMENT_STATUS_CONFIG = {
  unpaid: { color: '#ef4444', bgColor: '#fee2e2' },
  partial: { color: '#f59e0b', bgColor: '#fef3c7' },
  paid: { color: '#10b981', bgColor: '#d1fae5' },
};

const ORDER_STATUS_ICONS = {
  offline_completed: CheckCircle,
  offline_voided: XCircle,
  offline_on_hold: Clock,
  offline_credit: Wallet,
  offline_returned: RotateCcw,
  offline_partially_returned: RotateCcw,
  pending: Clock,
  in_courier: Truck,
};

// ─── Customer Filter ───────────────────────────────────────────────────────────
const CustomerFilter = ({ selectedCustomerId, onChange }) => {
  const { t } = useLocalization();
  const [searchTerm, setSearchTerm]         = useState('');
  const [searchResults, setSearchResults]   = useState([]);
  const [isSearching, setIsSearching]       = useState(false);
  const [showResults, setShowResults]       = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const searchTimeoutRef = useRef(null);
  const wrapperRef       = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Clear internal state when filter is reset externally
  useEffect(() => {
    if (!selectedCustomerId) setSelectedCustomer(null);
  }, [selectedCustomerId]);

  const performSearch = async (term) => {
    if (!term.trim()) { setSearchResults([]); setShowResults(false); return; }
    try {
      setIsSearching(true);
      const response = await getCustomers({ search: term.trim(), limit: 10, page: 1 });
      setSearchResults(response.customers || []);
      setShowResults(true);
    } catch (err) {
      console.error('Customer search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => performSearch(value), 300);
  };

  const handleSelect = (customer) => {
    setSelectedCustomer(customer);
    onChange(customer.id);
    setSearchTerm('');
    setSearchResults([]);
    setShowResults(false);
  };

  const handleClear = () => {
    setSelectedCustomer(null);
    onChange('');
    setSearchTerm('');
    setSearchResults([]);
  };

  if (selectedCustomer) {
    return (
      <div className={styles.customerFilterSelected}>
        <User size={13} />
        <span>{selectedCustomer.name}</span>
        {selectedCustomer.phone && (
          <span className={styles.customerFilterPhone}>{selectedCustomer.phone}</span>
        )}
        <button type="button" onClick={handleClear} className={styles.customerFilterClear}>
          <X size={12} />
        </button>
      </div>
    );
  }

  return (
    <div className={styles.customerFilter} ref={wrapperRef}>
      <div className={styles.customerFilterInputWrapper}>
        <Search size={14} className={styles.customerFilterSearchIcon} />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => searchResults.length > 0 && setShowResults(true)}
          placeholder={t('offlineSales.searchCustomers')}
          className={styles.customerFilterInput}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => { setSearchTerm(''); setSearchResults([]); setShowResults(false); }}
            className={styles.customerFilterInputClear}
          >
            <X size={12} />
          </button>
        )}
      </div>

      {showResults && (
        <div className={styles.customerFilterDropdown}>
          {isSearching ? (
            <div className={styles.customerFilterLoading}>
              <div className={styles.customerFilterSpinner} />
              <span>Searching…</span>
            </div>
          ) : searchResults.length > 0 ? (
            searchResults.map((customer) => (
              <div
                key={customer.id}
                className={styles.customerFilterOption}
                onClick={() => handleSelect(customer)}
              >
                <div className={styles.customerFilterOptionName}>
                  <User size={13} />{customer.name}
                </div>
                {customer.phone && (
                  <div className={styles.customerFilterOptionMeta}>{customer.phone}</div>
                )}
              </div>
            ))
          ) : (
            <div className={styles.customerFilterEmpty}>
              <User size={15} /><span>{t('offlineSales.noCustomersFound')}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Export Progress Modal ─────────────────────────────────────────────────────
const ExportProgressModal = ({ isOpen, onClose, status, error, totalRecords, fetchedRecords }) => {
  if (!isOpen) return null;

  const pct       = totalRecords > 0 ? Math.round((fetchedRecords / totalRecords) * 100) : 0;
  const isDone    = status === 'done';
  const isFailed  = status === 'error';
  const isRunning = status === 'running';

  return (
    <div className={styles.exportModalOverlay}>
      <div className={styles.exportModal}>
        <div className={styles.exportModalHeader}>
          <div className={styles.exportModalTitle}>
            <Download size={20} />
            <span>Exporting POS Orders</span>
          </div>
          {(isDone || isFailed) && (
            <button onClick={onClose} className={styles.exportModalClose}><X size={18} /></button>
          )}
        </div>

        <div className={styles.exportModalBody}>
          {isRunning && <div className={styles.exportSpinner}><Loader2 size={32} className={styles.spinnerIcon} /></div>}
          {isDone    && <div className={styles.exportSuccessIcon}><CheckCircle size={32} /></div>}
          {isFailed  && <div className={styles.exportErrorIcon}><AlertCircle size={32} /></div>}

          <p className={styles.exportStatusText}>
            {isFailed
              ? error || 'Export failed. Please try again.'
              : isDone
              ? 'Export complete! Your file has been downloaded.'
              : `Fetching orders… ${fetchedRecords.toLocaleString()} of ${totalRecords > 0 ? totalRecords.toLocaleString() : '?'}`}
          </p>

          {!isFailed && totalRecords > 0 && (
            <>
              <div className={styles.exportProgressBar}>
                <div
                  className={`${styles.exportProgressFill} ${isDone ? styles.exportProgressDone : ''}`}
                  style={{ width: `${isDone ? 100 : pct}%` }}
                />
              </div>
              <p className={styles.exportProgressPct}>{isDone ? 100 : pct}%</p>
            </>
          )}

          {!isFailed && totalRecords === 0 && isRunning && (
            <div className={styles.exportProgressBar}>
              <div className={styles.exportProgressIndeterminate} />
            </div>
          )}
        </div>

        {(isDone || isFailed) && (
          <div className={styles.exportModalFooter}>
            <button onClick={onClose} className={styles.cancelBtn}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Void Reason Modal ────────────────────────────────────────────────────────
const VoidReasonModal = ({ order, onConfirm, onClose, loading }) => {
  const { t } = useLocalization();
  const [reason, setReason] = useState('');
  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.voidModal} onClick={e => e.stopPropagation()}>
        <div className={styles.voidHeader}>
          <h3>{t('offlineSales.voidOrderTitle')}</h3>
          <button onClick={onClose} className={styles.iconBtn}><X size={16} /></button>
        </div>
        <div className={styles.voidBody}>
          <p className={styles.voidWarning}>{t('offlineSales.voidWarning')}</p>
          <label>{t('offlineSales.voidReason')} *</label>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            className={styles.textarea}
            placeholder={t('offlineSales.voidReasonPlaceholder')}
            autoFocus
          />
        </div>
        <div className={styles.voidFooter}>
          <button onClick={onClose} className={styles.cancelBtn}>{t('common.cancel', 'Cancel')}</button>
          <button
            disabled={!reason.trim() || loading}
            onClick={() => onConfirm(order.id, reason)}
            className={styles.dangerBtn}
          >
            {loading ? '...' : t('offlineSales.confirmVoid')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────
const OfflineSalesOrderListPage = () => {
  const navigate = useNavigate();
  const { t, formatCurrency } = useLocalization();
  const { success, error } = useAlert();
  const { hasPermission } = usePermissions();

  const [orders, setOrders]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [shops, setShops]     = useState([]);
  const [businessCouriers, setBusinessCouriers] = useState([]);
  const [courierStatuses, setCourierStatuses]   = useState([]);

  const [pagination, setPagination] = useState({ currentPage: 1, pageSize: 20, total: 0, pageSizeOptions: [10, 20, 50, 100] });
  const [sorting, setSorting]       = useState({ orderBy: 'createdAt', orderDirection: 'DESC' });

  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const [showFilters, setShowFilters]       = useState(false);
  const [voidTarget, setVoidTarget]         = useState(null);
  const [voidLoading, setVoidLoading]       = useState(false);
  const [paymentTarget, setPaymentTarget]   = useState(null);
  const [printOrder, setPrintOrder]         = useState(null);
  const [selectedOrder, setSelectedOrder]   = useState(null);
  const [trackingOrder, setTrackingOrder]   = useState(null);
  const [copiedKey, setCopiedKey]           = useState(null);

  const handleCopyAirwaybill = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      error('Failed to copy to clipboard');
    }
  };

  // Export state
  const [exportModalOpen, setExportModalOpen]           = useState(false);
  const [exportStatus, setExportStatus]                 = useState('idle');
  const [exportError, setExportError]                   = useState('');
  const [exportTotalRecords, setExportTotalRecords]     = useState(0);
  const [exportFetchedRecords, setExportFetchedRecords] = useState(0);
  const exportAbortRef = useRef(false);

  // Load reference data for filters
  useEffect(() => {
    getShops({ type: 'pos', limit: 100 }).then(r => setShops(r || [])).catch(() => {});
    getBusinessCouriers().then(r => setBusinessCouriers(r?.couriers || [])).catch(() => {});
    getCourierStatuses().then(r => setCourierStatuses(r?.courierStatuses || [])).catch(() => {});
  }, []);

  // Build filter params (shared by load and export)
  const buildFilterParams = () => {
    const params = {
      orderBy: sorting.orderBy,
      orderDirection: sorting.orderDirection,
    };
    if (filters.search)       params.search       = filters.search;
    if (filters.statusKeys.length > 0) params.statusKeys = filters.statusKeys.join(',');
    if (filters.paymentStatus) params.paymentStatus = filters.paymentStatus;
    if (filters.shopIds.length > 0)    params.shopIds    = filters.shopIds.join(',');
    if (filters.businessCourierIds.length > 0) params.businessCourierIds = filters.businessCourierIds.join(',');

    const isNotUpdated = filters.courierStatusIds.includes(NOT_UPDATED_VALUE);
    const realCourierStatusIds = filters.courierStatusIds.filter(id => id !== NOT_UPDATED_VALUE);
    if (isNotUpdated) {
      params.courierStatusNotUpdated = true;
    } else if (realCourierStatusIds.length > 0) {
      params.courierStatusIds = realCourierStatusIds.join(',');
    }

    if (filters.dateFrom)     params.dateFrom     = filters.dateFrom;
    if (filters.dateTo)       params.dateTo       = filters.dateTo;
    if (filters.customerId)   params.customerId   = filters.customerId;
    if (filters.sessionId)    params.sessionId    = filters.sessionId;
    return params;
  };

  const loadOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.pageSize,
        ...buildFilterParams(),
      };
      const result = await getOrders(params);
      setOrders(result.orders || []);
      setPagination(p => ({ ...p, total: result.total || 0 }));
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, sorting, filters, error]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadOrders(); }, [loadOrders]);

  // ─── Export ───────────────────────────────────────────────────────────────
  const MAX_EXPORT = 100000;
  const EXPORT_BATCH = 500;

  const handleExport = async () => {
    setExportModalOpen(true);
    setExportStatus('running');
    setExportError('');
    setExportTotalRecords(0);
    setExportFetchedRecords(0);
    exportAbortRef.current = false;

    try {
      const filterParams = buildFilterParams();
      const firstPage    = await getOrders({ ...filterParams, page: 1, limit: 1 });
      const total        = Math.min(firstPage.total || 0, MAX_EXPORT);

      if (total === 0) {
        setExportStatus('error');
        setExportError('No orders to export with the current filters.');
        return;
      }

      setExportTotalRecords(total);
      const allOrders  = [];
      const totalPages = Math.ceil(total / EXPORT_BATCH);

      for (let page = 1; page <= totalPages; page++) {
        if (exportAbortRef.current) return;
        const response = await getOrders({ ...filterParams, page, limit: EXPORT_BATCH });
        allOrders.push(...(response.orders || []));
        setExportFetchedRecords(Math.min(allOrders.length, total));
      }

      const canViewFinancials = hasPermission('manage_offline_sales_settings');

      const headers = [
        'Order #', 'Customer', 'Customer Phone', 'Cashier', 'Shop',
        'Total Qty', 'Grand Total', 'Paid', 'Balance',
        ...(canViewFinancials ? ['Payment Fees', 'Product Cost', 'Profit'] : []),
        'Status', 'Payment Status', 'Date',
      ];

      const escapeCell = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

      const rows = allOrders.map(order => {
        const totalQty = (order.orderItems || []).reduce((s, i) => s + parseFloat(i.quantity || 0), 0);
        const profit = parseFloat(order.grandTotal || 0)
          - parseFloat(order.totalTax || 0)
          - parseFloat(order.totalProductCost || 0)
          - parseFloat(order.totalFees || 0);
        return [
          order.orderNumber,
          order.customer?.name || 'Cash Sales',
          order.customer?.phone || '',
          order.cashier?.name || '',
          order.Shop?.name || '',
          totalQty,
          order.grandTotal || 0,
          order.paidAmount || 0,
          order.balanceAmount || 0,
          ...(canViewFinancials ? [
            order.totalFees || 0,
            order.totalProductCost || 0,
            profit,
          ] : []),
          order.OrderStatus?.key,
          order.paymentStatus,
          formatDateTime(order.createdAt),
        ].map(escapeCell).join(',');
      });

      const csvContent = [headers.map(escapeCell).join(','), ...rows].join('\n');
      const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = `pos-orders-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportStatus('done');
    } catch (err) {
      console.error('Export error:', err);
      setExportStatus('error');
      setExportError('Failed to export orders. Please try again.');
    }
  };

  const handleCloseExportModal = () => {
    exportAbortRef.current = true;
    setExportModalOpen(false);
    setExportStatus('idle');
  };

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleVoid = async (orderId, reason) => {
    try {
      setVoidLoading(true);
      await voidOrder(orderId, reason);
      success('Order voided successfully');
      setVoidTarget(null);
      loadOrders();
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to void order');
    } finally {
      setVoidLoading(false);
    }
  };

  const handlePrint = async (order) => {
    try {
      const result = await getOrder(order.id);
      setPrintOrder(result.order || result);
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to load order for printing');
    }
  };

  const handleProcessReturn = (order) => {
    navigate(`/offline-sales/return-notes/create?orderNumber=${order.orderNumber}`);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(p => ({ ...p, currentPage: 1 }));
  };

  const handleSearchChange = (searchValue) => {
    setFilters(prev => ({ ...prev, search: searchValue }));
    setPagination(p => ({ ...p, currentPage: 1 }));
  };

  const handlePageSizeChange = (newSize) => {
    setPagination(p => ({ ...p, pageSize: newSize, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setPagination(p => ({ ...p, currentPage: 1 }));
  };

  // ─── Filter definitions ───────────────────────────────────────────────────
  const courierStatusOptions = courierStatuses.length > 0
    ? [
        { id: NOT_UPDATED_VALUE, label: 'Not Updated' },
        ...courierStatuses.map(cs => ({
          id: cs.id,
          label: `${cs.statusLabel || cs.statusCode} - ${cs.courierCompany?.name || 'N/A'}`
        }))
      ]
    : [];

  const filterConfig = [
    {
      key: 'statusKeys',
      type: 'multiselect',
      label: t('offlineSales.filterByStatus'),
      placeholder: t('common.all', 'All'),
      options: [
        { key: 'completed',          label: t('offlineSales.statusCompleted') },
        { key: 'voided',             label: t('offlineSales.statusVoided') },
        { key: 'on_hold',            label: t('offlineSales.statusOnHold') },
        { key: 'credit',             label: t('offlineSales.statusCredit') },
        { key: 'returned',           label: t('offlineSales.statusReturned') },
        { key: 'partially_returned', label: t('offlineSales.statusPartiallyReturned') },
        { key: 'pending',            label: t('offlineSales.statusPending') },
        { key: 'in_courier',         label: t('offlineSales.statusInCourier') },
      ],
      displayKey: 'label',
      valueKey: 'key',
      searchKey: 'label',
    },
    {
      key: 'paymentStatus',
      type: 'custom',
      label: t('offlineSales.filterByPayment'),
      render: (value, onChange) => (
        <Dropdown
          value={value}
          onChange={(v) => onChange(v || '')}
          options={[
            { value: '', label: t('common.all', 'All') },
            { value: 'paid',    label: t('offlineSales.paymentPaid') },
            { value: 'partial', label: t('offlineSales.paymentPartial') },
            { value: 'unpaid',  label: t('offlineSales.paymentUnpaid') },
          ]}
          placeholder={t('common.all', 'All')}
          searchable
          allowClear
        />
      ),
    },
    {
      key: 'shopIds',
      type: 'multiselect',
      label: t('offlineSales.filterByShop'),
      placeholder: t('common.all', 'All'),
      options: shops,
      displayKey: 'name',
      valueKey: 'id',
      searchKey: 'name',
    },
    {
      key: 'businessCourierIds',
      label: t('ecomOrders.businessCourier'),
      type: 'multiselect',
      placeholder: t('ecomOrders.allBusinessCouriers'),
      options: businessCouriers.map(bc => ({
        id: bc.id,
        name: `${bc.accountName} (${bc.CourierCompany?.name || 'N/A'})`
      })),
      displayKey: 'name',
      valueKey: 'id',
      searchKey: 'name'
    },
    {
      key: 'courierStatusIds',
      label: t('ecomOrders.courierStatus'),
      type: 'multiselect',
      placeholder: t('ecomOrders.allCourierStatuses'),
      options: courierStatusOptions,
      displayKey: 'label',
      valueKey: 'id',
      searchKey: 'label'
    },
    {
      key: 'customerId',
      label: t('offlineSales.filterByCustomer'),
      type: 'custom',
      render: (value, onChange) => (
        <CustomerFilter selectedCustomerId={value} onChange={onChange} />
      ),
    },
    {
      key: 'sessionId',
      label: t('offlineSales.filterBySession'),
      type: 'custom',
      render: (value, onChange) => (
        <SessionSelector value={value} onChange={onChange} />
      ),
    },
    {
      key: 'dateRange',
      type: 'custom',
      label: t('offlineSales.dateRange'),
      render: () => (
        <DateRangePicker
          value={{ from: filters.dateFrom, to: filters.dateTo }}
          onChange={({ from, to }) => handleFilterChange({ ...filters, dateFrom: from, dateTo: to })}
        />
      ),
    },
  ];

  // ─── Column definitions ───────────────────────────────────────────────────
  const columns = [
    {
      key: 'orderNumber',
      header: t('offlineSales.orderNumberCol'),
      width: '170px',
      render: (row) => <DocNumberCell number={row.orderNumber} to={`/offline-sales/orders/${row.id}`} />,
    },
    {
      key: 'customer',
      header: t('offlineSales.customerCol'),
      width: '190px',
      render: (row) => (
        <div className={`${tb.partyInfo} ${styles.customerCell}`}>
          <div className={`${tb.partyName} ${styles.customerNameRow}`}>
            <User size={14} className={styles.customerNameIcon} />
            <TruncatedText text={row.customer?.name || t('offlineSales.cashSales')} />
          </div>
          {row.customer?.phone && <div className={tb.partyMeta}><Phone size={12} />{row.customer.phone}</div>}
        </div>
      ),
    },
    {
      key: 'shop',
      header: t('common.shop'),
      width: '150px',
      render: (row) => row.Shop?.name
        ? <div className={tb.shopBadge}><Store size={12} />{row.Shop.name}</div>
        : <span className={tb.naText}>—</span>,
    },
    {
      key: 'cashier',
      header: t('offlineSales.cashierCol'),
      render: (row) => row.cashier?.name
        ? <div className={tb.createdByInfo}><UserCheck size={12} />{row.cashier.name}</div>
        : <span className={tb.naText}>—</span>,
    },
    {
      key: 'status',
      header: t('offlineSales.statusCol'),
      width: '160px',
      render: (row) => {
        const color = row.OrderStatus?.colorCode || 'var(--color-text-secondary)';
        const StatusIcon = ORDER_STATUS_ICONS[row.OrderStatus?.key] || Clock;
        return (
          <div className={styles.statusCell}>
            <div className={tb.statusBadge} style={{ backgroundColor: `${color}20`, borderColor: color, color }}>
              <StatusIcon size={14} />
              <span>{t(`offlineSales.status_${row.OrderStatus?.key}`, row.OrderStatus?.label)}</span>
            </div>
            {row.courierStatus && (
              <div className={styles.courierStatusBadge} title={row.courierStatus.statusLabel}>
                <span className={styles.courierStatusText}>{row.courierStatus.statusLabel}</span>
              </div>
            )}
          </div>
        );
      },
    },
    {
      key: 'courier',
      header: t('offlineSales.courier'),
      width: '180px',
      render: (row) => {
        const copyKey = `awb-${row.id}`;
        const isCopied = copiedKey === copyKey;
        return (
          <div className={styles.courierInfo}>
            {row.airwaybillNumber && (
              <div className={styles.airwaybillWrapper}>
                <button
                  type="button"
                  className={styles.airwaybillNumber}
                  onClick={(e) => { e.stopPropagation(); setTrackingOrder(row); }}
                  title={t('ecomOrders.viewPackageTimeline')}
                >
                  {row.airwaybillNumber}
                </button>
                <button className={styles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyAirwaybill(row.airwaybillNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy airwaybill number'}>
                  {isCopied ? <Check size={12} /> : <Copy size={12} />}
                </button>
              </div>
            )}
            <div className={styles.courierName}><Truck size={12} />{row.assignedCourierAccount?.accountName || t('common.na')}</div>
          </div>
        );
      },
    },
    {
      key: 'paymentStatus',
      header: t('offlineSales.paymentStatusCol'),
      width: '120px',
      render: (row) => {
        const info = PAYMENT_STATUS_CONFIG[row.paymentStatus] || PAYMENT_STATUS_CONFIG.unpaid;
        const PayIcon = PAYMENT_STATUS_ICONS[row.paymentStatus] || Clock;
        return (
          <div className={tb.statusBadge} style={{ backgroundColor: info.bgColor, borderColor: info.color, color: info.color }}>
            <PayIcon size={14} />
            <span>{t(`offlineSales.payment_${row.paymentStatus}`, row.paymentStatus)}</span>
          </div>
        );
      },
    },
    {
      key: 'itemCount',
      header: t('offlineSales.items'),
      width: '80px',
      align: 'center',
      render: (row) => {
        const totalQty = (row.orderItems || []).reduce((s, i) => s + parseFloat(i.quantity || 0), 0);
        return (
          <div className={styles.itemsCount}>
            <Package size={12} />
            {Number.isInteger(totalQty) ? totalQty : totalQty.toFixed(2)}
          </div>
        );
      },
    },
    {
      key: 'grandTotal',
      header: t('offlineSales.totalCol'),
      align: 'right',
      render: (row) => <div className={tb.totalAmount}>{formatCurrency(row.grandTotal)}</div>,
    },
    {
      key: 'paidAmount',
      header: t('offlineSales.paidCol'),
      align: 'right',
      render: (row) => <div className={tb.amountMuted}>{formatCurrency(row.paidAmount)}</div>,
    },
    {
      key: 'balanceAmount',
      header: t('offlineSales.balanceCol'),
      align: 'right',
      render: (row) => parseFloat(row.balanceAmount) > 0
        ? <span className={styles.balanceDue}>{formatCurrency(row.balanceAmount)}</span>
        : <span className={tb.amountMuted}>{formatCurrency(0)}</span>,
    },
    ...(hasPermission('manage_offline_sales_settings') ? [
      {
        key: 'totalFees',
        header: t('offlineSales.paymentFeesCol'),
        align: 'right',
        render: (row) => parseFloat(row.totalFees) > 0
          ? <span style={{ color: 'var(--color-warning)' }}>{formatCurrency(row.totalFees)}</span>
          : formatCurrency(0),
      },
      {
        key: 'totalProductCost',
        header: t('offlineSales.productCostCol'),
        align: 'right',
        render: (row) => formatCurrency(row.totalProductCost || 0),
      },
      {
        key: 'profit',
        header: t('offlineSales.profitCol'),
        align: 'right',
        render: (row) => {
          const profit = parseFloat(row.grandTotal || 0)
            - parseFloat(row.totalTax || 0)
            - parseFloat(row.totalProductCost || 0)
            - parseFloat(row.totalFees || 0);
          return (
            <span style={{ color: profit >= 0 ? 'var(--color-success)' : 'var(--color-error)', fontWeight: 600 }}>
              {formatCurrency(profit)}
            </span>
          );
        },
      },
    ] : []),
    {
      key: 'createdAt',
      header: t('offlineSales.date'),
      width: '140px',
      render: (row) => (
        <div className={tb.dateCell}>
          <div className={tb.dateInfo}><Calendar size={12} />{formatDate(row.createdAt)}</div>
          <div className={tb.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const renderActions = (row) => (
    <div className={tb.actionsWrapper}>
      <button className={`${tb.actionButton} ${tb.viewButton}`} onClick={(e) => { e.stopPropagation(); setSelectedOrder(row); }} title={t('offlineSales.viewOrder')}>
        <Eye size={14} />
      </button>
      {!row.OrderStatus?.isClosed && hasPermission('edit_offline_sales_order') && (
        <button className={`${tb.actionButton} ${tb.editButton}`} onClick={(e) => { e.stopPropagation(); navigate(`/offline-sales/orders/${row.id}/edit`); }} title={t('common.edit')}>
          <Pencil size={14} />
        </button>
      )}
      <button className={`${tb.actionButton} ${tb.viewButton}`} onClick={(e) => { e.stopPropagation(); handlePrint(row); }} title={t('offlineSales.printReceiptAction')}>
        <Printer size={14} />
      </button>
      {row.OrderStatus?.key !== 'offline_voided' && row.OrderStatus?.key !== 'offline_returned' && hasPermission('void_offline_sales_order') && (
        <button className={`${tb.actionButton} ${tb.actionDanger}`} onClick={(e) => { e.stopPropagation(); setVoidTarget(row); }} title={t('offlineSales.voidOrder')}>
          <XCircle size={14} />
        </button>
      )}
      {(row.paymentStatus === 'partial' || row.paymentStatus === 'unpaid') && row.OrderStatus?.key !== 'offline_voided' && (
        <button className={`${tb.actionButton} ${tb.actionSuccess}`} onClick={(e) => { e.stopPropagation(); setPaymentTarget(row); }} title={t('offlineSales.recordPayment')}>
          <CreditCard size={14} />
        </button>
      )}
      {(row.OrderStatus?.key === 'offline_completed' || row.OrderStatus?.key === 'offline_partially_returned') && hasPermission('create_return_note') && (
        <button className={`${tb.actionButton} ${tb.actionWarning}`} onClick={(e) => { e.stopPropagation(); handleProcessReturn(row); }} title={t('offlineSales.processReturn')}>
          <RotateCcw size={14} />
        </button>
      )}
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('offlineSales.allPosOrders')}
        description={t('offlineSales.viewAndManageAllOrders')}
        icon={ShoppingBag}
        actions={
          <div className={styles.headerActions}>
            <button className={styles.refreshBtn} onClick={loadOrders} title={t('common.refresh', 'Refresh')}>
              <RefreshCw size={16} />
            </button>
            {hasPermission('create_offline_sales_order') && (
              <button className={styles.primaryBtn} onClick={() => navigate('/offline-sales/orders/create')}>
                <Plus size={16} /> {t('offlineSales.createOfflineSalesOrder')}
              </button>
            )}
          </div>
        }
      />

      <FilterPanel
        filters={filterConfig}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(v => !v)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t('common.export', 'Export')}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('offlineSales.searchOrdersPlaceholder'),
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions,
        }}
      />

      <DataTable
        data={orders}
        columns={columns}
        actions={{ render: renderActions, width: '190px' }}
        loading={loading}
        emptyMessage={t('offlineSales.allPosOrders')}
        emptyDescription={t('offlineSales.searchOrdersPlaceholder')}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={{ ...pagination, pageSizeOptions: pagination.pageSizeOptions }}
        onPaginationChange={(changes) => setPagination(p => ({ ...p, ...changes }))}
        onRowClick={(row) => setSelectedOrder(row)}
      />

      {voidTarget && (
        <VoidReasonModal order={voidTarget} onConfirm={handleVoid} onClose={() => setVoidTarget(null)} loading={voidLoading} />
      )}

      {paymentTarget && (
        <OfflineSalesPaymentRecordsModal order={paymentTarget} onClose={() => setPaymentTarget(null)} onSaved={loadOrders} />
      )}

      {trackingOrder && (
        <AirwaybillTrackingModal
          orderId={trackingOrder.id}
          airwaybillNumber={trackingOrder.airwaybillNumber}
          courierName={trackingOrder.assignedCourierAccount?.accountName}
          onClose={() => setTrackingOrder(null)}
        />
      )}

      {printOrder && (
        <OrderReceiptPrintModal order={printOrder} onClose={() => setPrintOrder(null)} />
      )}

      {selectedOrder && (
        <OfflineSalesOrderDetailsModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
      )}

      <ExportProgressModal
        isOpen={exportModalOpen}
        onClose={handleCloseExportModal}
        status={exportStatus}
        error={exportError}
        totalRecords={exportTotalRecords}
        fetchedRecords={exportFetchedRecords}
      />
    </div>
  );
};

export default OfflineSalesOrderListPage;
